import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';

import Link from './Link';
import MenuButton from './MenuButton';
import { useHeader } from './hooks';
import { LayoutHeaderProps } from './types';
import { HEADER_LINK_TYPE } from './constants';
import LinksList from './LinksList';
import Logos from './Logos';
import LanguageLinks from './LanguageLinks';

import { LAYOUT_VARIABLES } from '../constants';

import { GLOBAL_STYLE_VARIABLES } from 'theme';
import { Classname } from 'utils/types';
import { filterProps } from 'utils/helpers';

type THeader = Classname & LayoutHeaderProps;

export enum HEADER_VARIABLES {
  baseDarkColor = '--base-dark-color',
  baseLightColor = '--base-light-color',
  shadowColor = '--shadow-color',
  transitionDuration = '--transition-duration',
  backgroundAlpha = '--background-alpha',
  backgroundColor = '--background-color',
  backdrop = '--backdrop',
  accentColor = '--accent-color',
  overlay = '--overlay',
  textColor = '--text-color',
  textActiveColor = '--text-active-color',
  verticalPadding = '--vertical-padding',
  logoColor = '--logoColor',
}

const Container = styled('header', filterProps('showHeaderBackground', 'isDark'))<{
  showHeaderBackground: boolean;
  isDark: boolean;
}>`
  ${({ theme: { colors, durations, mq }, showHeaderBackground, isDark }) => css`
    ${HEADER_VARIABLES.baseDarkColor}: 24, 24, 24;
    ${HEADER_VARIABLES.baseLightColor}: 217, 217, 217;
    ${HEADER_VARIABLES.transitionDuration}: ${durations.regular};
    ${HEADER_VARIABLES.shadowColor}: #00000040;
    ${HEADER_VARIABLES.backgroundAlpha}: 0.9;
    ${HEADER_VARIABLES.backgroundColor}: rgba(var(${isDark
      ? HEADER_VARIABLES.baseDarkColor
      : HEADER_VARIABLES.baseLightColor}), var(${HEADER_VARIABLES.backgroundAlpha}));
    ${HEADER_VARIABLES.backdrop}: none;
    ${HEADER_VARIABLES.accentColor}: rgb(var(${isDark
      ? HEADER_VARIABLES.baseLightColor
      : HEADER_VARIABLES.baseDarkColor}));
    ${HEADER_VARIABLES.overlay}: linear-gradient(90deg, var(${HEADER_VARIABLES.accentColor}) -400%, transparent 80%);
    ${HEADER_VARIABLES.textColor}: ${isDark ? '#eaeaeab3' : '#898989'};
    ${HEADER_VARIABLES.textActiveColor}: ${isDark ? '#eaeaea' : '#898989'};
    ${HEADER_VARIABLES.verticalPadding}: var(${LAYOUT_VARIABLES.headerVerticalPadding});
    ${HEADER_VARIABLES.logoColor}: ${isDark ? colors.white : colors.black};

    width: 100%;

    ${mq.mdMin} {
      ${HEADER_VARIABLES.backgroundAlpha}: 0.5;
      ${HEADER_VARIABLES.backdrop}: blur(1rem);

      ${!showHeaderBackground
        ? css`
            ${HEADER_VARIABLES.transitionDuration}: ${durations.long};
            ${HEADER_VARIABLES.backgroundColor}: transparent;
            ${HEADER_VARIABLES.backdrop}: blur(0);
            ${HEADER_VARIABLES.shadowColor}: transparent;
            ${HEADER_VARIABLES.overlay}: transparent;
          `
        : css`
            ${HEADER_VARIABLES.verticalPadding}: .5rem;

            ${!isDark
              ? css`
                  ${HEADER_VARIABLES.textColor}: #353535;
                `
              : ''}
          `}
    }
  `}
`;

const StyledHeader = styled.div`
  ${({ theme: { depths, durations, mq, spacings } }) => css`
    box-sizing: border-box;
    display: flex;
    position: fixed;
    align-items: center;
    justify-content: space-between;
    transition: background-color var(${HEADER_VARIABLES.transitionDuration}) ease-out,
      backdrop-filter ${durations.fade} ease,
      padding var(${HEADER_VARIABLES.transitionDuration}) ease;
    z-index: ${depths.menu};
    background-color: var(${HEADER_VARIABLES.backgroundColor});
    padding: var(${HEADER_VARIABLES.verticalPadding})
      calc(7.5rem + var(${GLOBAL_STYLE_VARIABLES.scrollbarCompensation}))
      var(${HEADER_VARIABLES.verticalPadding}) 7.5rem;
    width: 100%;
    backdrop-filter: var(${HEADER_VARIABLES.backdrop});

    &::after {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      transform: rotate(180deg);
      z-index: ${depths.menu + 2};
      box-shadow: 0 0 2rem var(${HEADER_VARIABLES.shadowColor});
      background: var(${HEADER_VARIABLES.overlay});
      width: 100%;
      height: 100%;
      content: '';
      pointer-events: none;
    }

    ${mq.md} {
      padding: var(${LAYOUT_VARIABLES.headerVerticalPadding}) min(2rem, ${spacings.padding});
    }
  `}
`;

const backdropBlurDelay = keyframes`
  ${css`
    0% {
      backdrop-filter: initial;
    }

    100% {
      backdrop-filter: blur(0.25rem);
    }
  `}

`;

const LinksWrapper = styled('nav', filterProps('open'))<{ open: boolean }>`
  ${({ theme: { depths, durations, mq }, open }) => css`
    z-index: ${depths.menu + 3};

    ${mq.md} {
      display: flex;
      position: absolute;
      top: var(${LAYOUT_VARIABLES.headerHeight});
      right: 0;
      justify-content: flex-end;
      transition: width ${durations.regular};
      cursor: pointer;
      padding: 0;
      width: 0;
      height: calc(100vh - var(${LAYOUT_VARIABLES.headerHeight}));
      overflow: hidden;

      ${open
        ? css`
            width: 100%;
            animation: ${backdropBlurDelay} ${durations.regular} forwards;
          `
        : ''}
    }
  `}
`;

const LinksContainer = styled.ul`
  display: flex;
  align-items: center;
  margin: 0;

  ${({ theme: { colors, depths, durations, mq, spacings, utils } }) => css`
    ${mq.md} {
      display: grid;
      top: 0;
      right: 0;
      row-gap: 1rem;
      align-content: flex-start;
      transition: width ${durations.regular};
      z-index: ${depths.menu};
      box-shadow: 0 -2rem 2rem ${colors.black}80;
      background: var(${HEADER_VARIABLES.backgroundColor});
      cursor: default;
      padding: 0;
      width: 70%;
      height: 100%;
      overflow-x: hidden;
      overflow-y: auto;

      ${utils.hideScroll}

      &::before {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        flex-direction: column;
        transform: rotate(180deg);
        transition: width ${durations.regular};
        z-index: -1;
        background: var(${HEADER_VARIABLES.overlay});
        width: 100vw;
        height: 100%;
        overflow: hidden;
        content: '';
        pointer-events: none;
        clip-path: polygon(0 0, 70% 0, 70% 100%, 0 100%);
      }

      & > :first-of-type {
        margin-top: ${spacings.padding};
      }
    }
  `}
`;

const LinkWrapper = styled.li`
  display: flex;
`;

const StyledLink = styled(Link)`
  ${({ theme: { colors, durations, mq } }) => css`
    position: relative;

    &::after {
      display: block;
      position: absolute;
      bottom: -2.25rem;
      left: 50%;
      transition: ${durations.long};
      opacity: 0;
      margin: 0 2rem;
      width: 0;
      height: 2.25rem;
      content: '';
    }

    &:hover {
      &::after {
        left: 0;
        opacity: 1;
        border-bottom: 3px solid ${colors.primary};
        width: calc(100% - (2rem * 2));
      }
    }

    ${mq.md} {
      &::after {
        display: none;
      }
    }
  `}
`;

const Header: React.FC<THeader> = ({ className, isDark = false, ...props }) => {
  const { open, closeModal, toggleModal, headerLogoImage, headerLinks, homeUrl, languageProps } =
    useHeader(props);
  const [showHeaderBackground, setShowHeaderBackground] = useState(false);

  useEffect(() => {
    const scrollListener = () => {
      const scrollNumber = window.scrollY;

      if (scrollNumber > 15) {
        setShowHeaderBackground(true);
      } else {
        setShowHeaderBackground(false);
      }
    };
    document.addEventListener('scroll', scrollListener);

    return () => {
      document.removeEventListener('scroll', scrollListener);
    };
  }, []);

  const [activeIndex, setActiveIndex] = useState(-1);

  const makeLinkListClickHandle = useCallback(
    (index: number) => () => {
      setActiveIndex(index === activeIndex ? -1 : index);
    },
    [activeIndex]
  );

  return (
    <Container className={className} showHeaderBackground={showHeaderBackground} isDark={isDark}>
      <StyledHeader>
        <Logos homeUrl={homeUrl} headerLogoImage={headerLogoImage} />
        <LinksWrapper open={open} onClick={closeModal}>
          <LinksContainer
            onClick={event => {
              event.stopPropagation();
            }}
          >
            {headerLinks.map((linkProps, index) =>
              linkProps.type === HEADER_LINK_TYPE.LIST ? (
                <LinksList
                  key={linkProps.label}
                  {...linkProps}
                  open={activeIndex === index}
                  onClick={makeLinkListClickHandle(index)}
                />
              ) : (
                <LinkWrapper key={linkProps.label}>
                  <StyledLink
                    external={linkProps.type === HEADER_LINK_TYPE.EXTERNAL}
                    href={linkProps.url}
                    onClick={closeModal}
                  >
                    {linkProps.label}
                  </StyledLink>
                </LinkWrapper>
              )
            )}
            <LanguageLinks {...languageProps} />
          </LinksContainer>
        </LinksWrapper>
        <MenuButton open={open} onToggle={toggleModal} />
      </StyledHeader>
    </Container>
  );
};

export default Header;
